import React, { useCallback } from 'react'

import useStyles from './styles'
import { Button } from '@refera/ui-web'
import { FormHelperText, Grid, Tooltip } from '@mui/material'
import Accordion from '_/views/finance/components/Accordion'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useToast } from '_/hooks/use-toast'
import { useDialog } from '_/hooks/use-dialog'
import { INSTALLMENT_TABLE_DEFAULT_ITEM } from './constants'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'
import IconButton from '_/components/svg/icon-button'
import { Trash } from '@refera/ui-icons'

export function Parcelamento() {
  const styles = useStyles()
  const { showToast } = useToast()
  const {
    control,
    formState: { errors },
  } = useFormContext()
  const { closeDialog, showDialog } = useDialog()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'agencyInterestRates',
    keyName: 'fieldId',
  })

  const handleAppendItem = useCallback(() => {
    if (fields.length < 24) {
      append(INSTALLMENT_TABLE_DEFAULT_ITEM)
    } else {
      showToast({
        type: 'warning',
        message: 'Você só pode inserir até 24 parcelas.',
      })
    }
  }, [append, fields, showToast])

  const handleRemove = useCallback(
    index => {
      showDialog({
        type: 'warning',
        subject: 'Remover parcela',
        description: 'Você tem certeza que deseja executar esta ação?',
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
        onCancel: closeDialog,
        onApprove: () => {
          remove(index)
          closeDialog()
        },
      })
    },
    [showDialog, closeDialog, remove]
  )

  return (
    <Accordion
      id="installments"
      title="Regras de parcelamento"
      defaultExpanded={false}
      className={styles.accordion}
    >
      <Grid container className={styles.container} flexWrap="nowrap">
        {fields?.map((item, index) => {
          const nameBase = `agencyInterestRates.${index}`
          const agencyInterestRatesErrors = errors?.agencyInterestRates

          return (
            <Grid key={item.fieldId ?? item.id ?? index} flexDirection="column" gap={2}>
              <Grid className={styles.row} container wrap="nowrap">
                <Grid
                  item
                  sx={{
                    flex: 2,
                    maxWidth: '300px',
                  }}
                >
                  <NumericInput
                    name={`${nameBase}.installment`}
                    label={index === 0 ? 'Parcelamento' : ''}
                    decimalScale={0}
                    defaultValue={item?.installment ?? 0}
                    hasError={agencyInterestRatesErrors?.[index]?.installment?.message}
                    placeholder="0"
                  />
                  {agencyInterestRatesErrors?.[index] && (
                    <FormHelperText
                      className={styles.errorMessage}
                      error={!!agencyInterestRatesErrors?.[index]}
                    >
                      {agencyInterestRatesErrors?.[index]?.installment?.message}
                    </FormHelperText>
                  )}
                </Grid>
                <Grid
                  item
                  sx={{
                    flex: 4,
                    maxWidth: '300px',
                  }}
                >
                  <NumericInput
                    label={index === 0 ? 'Juros' : ''}
                    name={`${nameBase}.interestRate`}
                    placeholder="0,00%"
                    suffix="%"
                    defaultValue={item?.interestRate ?? item?.interest_rate ?? 0}
                    hasError={agencyInterestRatesErrors?.[index]?.interestRate?.message}
                  />
                  {agencyInterestRatesErrors?.[index] && (
                    <FormHelperText
                      className={styles.errorMessage}
                      error={!!agencyInterestRatesErrors?.[index]}
                    >
                      {agencyInterestRatesErrors?.[index]?.interestRate?.message}
                    </FormHelperText>
                  )}
                </Grid>

                <Grid item>
                  <Tooltip
                    title="Excluir parcelamento"
                    placement="top"
                    className={styles.removeButton}
                    style={{ marginTop: index === 0 ? '18px' : '-2px' }}
                  >
                    <IconButton variant="ghost" onClick={() => handleRemove(index)}>
                      <Trash color="red" fontSize={38} />
                    </IconButton>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          )
        })}
        <Button
          variant="secondary"
          onClick={handleAppendItem}
          style={{ width: '160px', marginTop: '8px' }}
        >
          + Adicionar item
        </Button>
      </Grid>
    </Accordion>
  )
}
